import React from "react"

import { BadgeCheck } from "lucide-react"

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useActiveJob } from "@/contexts/ActiveJobContext"
import { useApplication } from "@/contexts/ApplicationContext"
import { useIsMobile } from "@/hooks/use-mobile"

interface DocumentUploadTabsProps {
  activeTab: string
  onTabChange: (value: string) => void
  submittedTabs: string[]
  children: React.ReactNode
}

const DocumentUploadTabs: React.FC<DocumentUploadTabsProps> = ({
  activeTab,
  onTabChange,
  submittedTabs,
  children,
}) => {
  const isMobile = useIsMobile()
  const { job } = useActiveJob()
  const { application } = useApplication()
  const hideInsurance = application?.basicDetails?.needsVan === true
  // Build tab config from the order of segments in the 'documents' stage
  const segmentLabelMap: Record<string, string> = {
    "dvla-check": isMobile ? "DVLA" : "DVLA Check",
    "rtw-check": isMobile ? "RTW" : "Right to Work",
    "insurance-check": isMobile ? "Insurance" : "Insurance Check",
    "cpc-upload": isMobile ? "CPC" : "CPC Upload",
    "tacho-card-upload": isMobile ? "Tacho" : "Tacho Card Upload",
    "d4-medical-report-upload": isMobile ? "D4" : "D4 Upload",
  }

  const documentsStage = job.stages.find((s) => s.id === "documents")
  const tabConfig = (documentsStage?.segments || [])
    .map((seg) => ({
      value: seg.id,
      label: segmentLabelMap[seg.id] || seg.id,
      visible: seg.id === "insurance-check" ? !hideInsurance : true,
    }))
    // In case the documents stage is missing or empty, fall back to known order
    .concat(
      documentsStage?.segments?.length
        ? []
        : [
            "dvla-check",
            "rtw-check",
            "insurance-check",
            "cpc-upload",
            "tacho-card-upload",
            "d4-medical-report-upload",
          ].map((id) => ({
            value: id,
            label: segmentLabelMap[id] || id,
            visible:
              job.stages.some((stage) => stage.segments.some((segment) => segment.id === id)) &&
              (id !== "insurance-check" || !hideInsurance),
          })),
    )

  const visibleTabs = tabConfig.filter((tab) => tab.visible)
  return (
    <Tabs value={activeTab} onValueChange={onTabChange}>
      <div className="w-full overflow-x-auto">
        <TabsList className={`flex w-full ${isMobile ? "gap-1" : ""}`}>
          {visibleTabs.map((tab) => {
            const isSubmitted = submittedTabs.includes(tab.value)
            // Only the active tab is clickable
            const isDisabled = tab.value !== activeTab
            const triggerClasses = [
              "group flex-1 min-w-0",
              isSubmitted ? "bg-green-100" : "",
              // Keep completed tabs green without dimming; dim only incomplete non-active
              isDisabled && !isSubmitted ? "opacity-50 cursor-not-allowed" : "",
            ]
              .filter(Boolean)
              .join(" ")

            return (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className={triggerClasses}
                disabled={isDisabled}
              >
                <div
                  className="flex w-full min-w-0 items-center justify-center gap-1 overflow-hidden text-ellipsis whitespace-nowrap"
                  title={typeof tab.label === "string" ? tab.label : undefined}
                >
                  {isSubmitted ? (
                    <BadgeCheck className="h-4 w-4 shrink-0 text-green-600" aria-hidden="true" />
                  ) : null}
                  <span className="truncate">{tab.label}</span>
                </div>
              </TabsTrigger>
            )
          })}
        </TabsList>
      </div>
      {children}
    </Tabs>
  )
}

export default DocumentUploadTabs
